import axios from 'axios';

// API base URL
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504, 0]; // Include status 0 for network errors

// Simple in-memory cache for GET requests
const responseCache = new Map();
const CACHE_TTL = 30000; // 30 seconds cache
const CACHEABLE_PATTERNS = [
  /\/rpg\/character$/,
  /\/rpg\/daily-quests$/,
  /\/rpg\/battle-tier\/\w+\/monster/,
  /\/raid\/bosses$/,
  /\/cardology\/decks$/,
];

// Check if URL is cacheable
const isCacheable = (url) => {
  return CACHEABLE_PATTERNS.some(pattern => pattern.test(url));
};

// Get cache key
const getCacheKey = (config) => {
  return `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
};

// Clean expired cache entries
const cleanCache = () => {
  const now = Date.now();
  for (const [key, value] of responseCache.entries()) {
    if (now > value.expiry) {
      responseCache.delete(key);
    }
  }
};

// Periodically clean cache
setInterval(cleanCache, 60000);

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 30000, // 30 second timeout for better reliability
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Check if error is retryable
const isRetryableError = (error) => {
  // Network errors (no response)
  if (!error.response) return true;
  
  // Timeout errors
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;
  
  // Network errors
  if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') return true;
  
  // Retryable HTTP status codes
  if (RETRY_STATUS_CODES.includes(error.response?.status)) return true;
  
  return false;
};

// Request interceptor - add auth token, check cache
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('cardology_token') || sessionStorage.getItem('cardology_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add retry count to config
    config.retryCount = config.retryCount || 0;
    
    // Check cache for GET requests
    if (config.method === 'get' && isCacheable(config.url) && !config.skipCache) {
      const cacheKey = getCacheKey(config);
      const cached = responseCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        // Return cached response by setting adapter to resolve immediately
        config.adapter = () => Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (cached)',
          headers: {},
          config,
        });
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle retries and cache responses
apiClient.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    const config = response.config;
    if (config.method === 'get' && isCacheable(config.url) && !config.skipCache) {
      const cacheKey = getCacheKey(config);
      responseCache.set(cacheKey, {
        data: response.data,
        expiry: Date.now() + CACHE_TTL,
      });
    }
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // Don't retry if no config or max retries reached
    if (!config || config.retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }
    
    // Check if we should retry
    if (isRetryableError(error)) {
      config.retryCount += 1;
      
      // Exponential backoff with jitter
      const baseDelay = RETRY_DELAY * Math.pow(2, config.retryCount - 1);
      const jitter = Math.random() * 500; // Add random jitter up to 500ms
      const backoffDelay = baseDelay + jitter;
      
      await delay(backoffDelay);
      return apiClient(config);
    }
    
    return Promise.reject(error);
  }
);

// Invalidate cache for specific patterns
export const invalidateCache = (pattern) => {
  for (const key of responseCache.keys()) {
    if (key.includes(pattern)) {
      responseCache.delete(key);
    }
  }
};

// Clear all cache
export const clearCache = () => {
  responseCache.clear();
};

// Network status monitoring
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
const networkListeners = new Set();

// Listen to online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    networkListeners.forEach(listener => listener(true));
  });
  
  window.addEventListener('offline', () => {
    isOnline = false;
    networkListeners.forEach(listener => listener(false));
  });
}

// Subscribe to network status changes
export const onNetworkChange = (callback) => {
  networkListeners.add(callback);
  return () => networkListeners.delete(callback);
};

// Get current network status
export const getNetworkStatus = () => isOnline;

// Health check function
export const checkApiHealth = async () => {
  try {
    const response = await apiClient.get('/cardology/decks', { 
      timeout: 5000,
      skipCache: true
    });
    return Array.isArray(response.data);
  } catch {
    return false;
  }
};

// Export the configured client
export default apiClient;
